(() => {
  const REQUEST_TIMEOUT_MS = 20000;
  const RETRY_LIMIT = 3;
  const RETRY_DELAY_MS = 1500;
  // 実測の応答は 1 秒台。大きく超えたときだけ、待たせている旨に切り替える
  const SLOW_NOTICE_MS = 8000;
  // 応答しない相手に再試行は効かない。速く失敗する 429 / 5xx の 4 回（約 10 秒）は収まる
  const TOTAL_TIMEOUT_MS = 30000;
  const IMAGE_WIDTH = 600;
  const CAMPAIGN = 'image_search';
  const VENDOR_CONCURRENCY = 6;
  // スクロールが速いときに空欄が見えないよう、画面に入る手前から取りに行く。
  const VENDOR_ROOT_MARGIN = '200px';

  /** @param {string} value @returns {string|null} */
  function httpsUrl(value) {
    try {
      const url = new URL(value);
      return url.protocol === 'https:' && !url.username && !url.password ? url.href : null;
    } catch {
      return null;
    }
  }

  /** @param {string} value @returns {string|null} */
  function sizedImageUrl(value) {
    const href = httpsUrl(value);
    if (!href) return null;
    const url = new URL(href);
    // カタログの画像URLは変換パラメータを持たず原寸で返る。
    url.searchParams.set('width', String(IMAGE_WIDTH));
    return url.href;
  }

  /** @param {string} value @returns {string|null} */
  function campaignUrl(value) {
    const href = httpsUrl(value);
    if (!href) return null;
    const url = new URL(href);
    // カタログ側の計測を残したまま、自店の画像検索経由を判別できるようにする。
    url.searchParams.set('utm_campaign', CAMPAIGN);
    return url.href;
  }

  /** @param {number} milliseconds @param {AbortSignal} signal */
  function delay(milliseconds, signal) {
    return new Promise((resolve, reject) => {
      signal.throwIfAborted();
      const abort = () => {
        clearTimeout(timer);
        reject(signal.reason);
      };
      const timer = setTimeout(() => {
        signal.removeEventListener('abort', abort);
        resolve();
      }, milliseconds);
      signal.addEventListener('abort', abort, { once: true });
    });
  }

  class ImageSearch extends HTMLElement {
    #listeners;
    #operation;
    #image = null;
    #searched = false;

    connectedCallback() {
      if (this.#listeners) return;
      this.#listeners = new AbortController();
      const options = { signal: this.#listeners.signal };
      this.form = this.querySelector('[data-form]');
      this.file = this.querySelector('[data-file]');
      this.query = this.querySelector('[data-query]');
      this.preview = this.querySelector('[data-preview]');
      this.submit = this.querySelector('[data-submit]');
      this.reset = this.querySelector('[data-reset]');
      this.status = this.querySelector('[data-status]');
      this.results = this.querySelector('[data-results]');
      this.drop = this.querySelector('[data-drop]');

      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        if (this.#image) this.#search();
      }, options);
      this.file.addEventListener('change', () => {
        if (this.file.files[0]) this.#select(this.file.files[0]);
      }, options);
      this.reset.addEventListener('click', () => {
        this.#clear();
        this.file.focus();
      }, options);
      this.drop.addEventListener('click', () => {
        // 検索後はラベルを隠すため、点線の枠自体を選び直しの導線にする。
        // 検索前はラベルが枠を覆って既にクリックを受けており、二重に開いてしまう。
        if (!this.#searched) return;
        // 同じ画像を選び直したときに change が発火しなくなるのを防ぐ。
        this.file.value = '';
        this.file.click();
      }, options);
      this.drop.addEventListener('dragover', (event) => {
        event.preventDefault();
        this.drop.classList.add('is-dragging');
      }, options);
      this.drop.addEventListener('dragleave', (event) => {
        if (!this.drop.contains(event.relatedTarget)) this.drop.classList.remove('is-dragging');
      }, options);
      this.drop.addEventListener('drop', (event) => {
        event.preventDefault();
        this.drop.classList.remove('is-dragging');
        this.file.value = '';
        this.#select(event.dataTransfer.files.length === 1 ? event.dataTransfer.files[0] : null);
      }, options);
      this.#clear();
      // 初期化の途中で失敗したときに押せる見た目にしないよう、最後に有効化する
      this.querySelector('[data-fields]').disabled = false;
    }

    disconnectedCallback() {
      this.#operation?.abort();
      this.#listeners?.abort();
      this.#listeners = null;
      this.#image = null;
      this.preview?.removeAttribute('src');
      this.results?.replaceChildren();
    }

    /** @param {string} query */
    search(query) {
      this.query.value = query;
      if (this.#image) this.#search();
    }

    cancel() {
      this.#operation?.abort();
      this.results.replaceChildren();
      this.#state('', false);
    }

    #clear() {
      this.#operation?.abort();
      this.#image = null;
      this.#searched = false;
      this.form.reset();
      this.file.removeAttribute('aria-invalid');
      this.preview.removeAttribute('src');
      this.preview.hidden = true;
      this.reset.hidden = true;
      this.drop.classList.remove('is-dragging');
      this.results.replaceChildren();
      this.#state('', false);
    }

    /** @param {string} message @param {boolean} busy */
    #state(message, busy) {
      this.status.textContent = message;
      this.results.setAttribute('aria-busy', String(busy));
      this.submit.disabled = busy || !this.#image;
      // キーワードは画像検索の追加条件なので、1度検索するまで出さない。
      this.classList.toggle('is-searched', this.#searched);
      this.dispatchEvent(new CustomEvent('image-search:state', {
        bubbles: true, detail: { hasImage: Boolean(this.#image), busy, searched: this.#searched },
      }));
    }

    /** @param {File|null} file */
    async #select(file) {
      // 検証を先に行い、画像でないドロップでは表示中の結果を残す。
      if (!file?.type.startsWith('image/') || !file.size) {
        this.file.setAttribute('aria-invalid', 'true');
        this.#state(this.dataset.invalid, false);
        return;
      }
      this.#operation?.abort();
      const operation = new AbortController();
      this.#operation = operation;
      this.#image = null;
      this.preview.removeAttribute('src');
      this.preview.hidden = true;
      this.results.replaceChildren();
      this.reset.hidden = false;
      this.file.removeAttribute('aria-invalid');
      this.#state(this.dataset.preparing, true);
      try {
        const source = new Image();
        const objectUrl = URL.createObjectURL(file);
        try {
          await new Promise((resolve, reject) => {
            const abort = () => {
              source.src = '';
              reject(operation.signal.reason);
            };
            operation.signal.addEventListener('abort', abort, { once: true });
            source.onload = () => {
              operation.signal.removeEventListener('abort', abort);
              resolve();
            };
            source.onerror = () => {
              operation.signal.removeEventListener('abort', abort);
              reject(new Error('invalid'));
            };
            source.src = objectUrl;
          });
          operation.signal.throwIfAborted();
          const maxEdge = Math.min(2048, Math.max(512, Number(this.dataset.maxEdge) || 1024));
          const ratio = Math.min(1, maxEdge / Math.max(source.naturalWidth, source.naturalHeight));
          const canvas = document.createElement('canvas');
          canvas.width = Math.max(1, Math.round(source.naturalWidth * ratio));
          canvas.height = Math.max(1, Math.round(source.naturalHeight * ratio));
          const context = canvas.getContext('2d');
          // JPEGの透明部分が黒くなると類似検索の背景色が変わるため、白に合成する。
          context.fillStyle = '#fff';
          context.fillRect(0, 0, canvas.width, canvas.height);
          context.drawImage(source, 0, 0, canvas.width, canvas.height);
          const image = canvas.toDataURL('image/jpeg', 0.85);
          if (!image.startsWith('data:image/jpeg;base64,')) throw new Error('invalid');
          this.#image = image.split(',')[1];
          this.preview.src = image;
          this.preview.hidden = false;
          canvas.width = canvas.height = 0;
        } finally {
          URL.revokeObjectURL(objectUrl);
          source.onload = source.onerror = null;
        }
        await this.#search();
      } catch {
        if (operation.signal.aborted) return;
        this.file.setAttribute('aria-invalid', 'true');
        this.#state(this.dataset.invalid, false);
      }
    }

    async #search() {
      this.#operation?.abort();
      const operation = new AbortController();
      this.#operation = operation;
      this.results.replaceChildren();
      this.#state(this.dataset.searching, true);
      let timedOut = false;
      const slowNotice = setTimeout(() => this.#state(this.dataset.slow, true), SLOW_NOTICE_MS);
      const deadline = setTimeout(() => {
        timedOut = true;
        operation.abort();
      }, TOTAL_TIMEOUT_MS);
      try {
        const endpoint = httpsUrl(this.dataset.endpoint);
        const profile = httpsUrl(this.dataset.profile);
        if (!endpoint || !profile || !/^gid:\/\/shopify\/Shop\/\d+$/.test(this.dataset.shopId)) {
          throw new Error('config');
        }
        const catalog = {
          like: [{ image: { content_type: 'image/jpeg', data: this.#image } }],
          context: {
            address_country: this.dataset.country,
            language: this.dataset.language,
            currency: this.dataset.currency,
          },
          filters: { available: true, shops: [this.dataset.shopId] },
          pagination: { limit: Math.min(50, Math.max(1, Number(this.dataset.limit) || 24)) },
        };
        if (this.query.value.trim()) catalog.query = this.query.value.trim();
        const body = JSON.stringify({
          jsonrpc: '2.0', id: 1, method: 'tools/call',
          params: { name: 'search_catalog', arguments: { meta: { 'ucp-agent': { profile } }, catalog } },
        });
        const products = await this.#request(endpoint, body, operation.signal);
        operation.signal.throwIfAborted();
        this.#searched = true;
        const { count, pending } = this.#render(products);
        // 応答はあるのに 1 件も描画できないのは形式の変化なので、0 件と区別する。
        if (!count && products.length) {
          this.#state(this.dataset.failed, false);
          return;
        }
        const complete = count === 1 ? this.dataset.completeOne : this.dataset.completeOther;
        this.#state(count ? complete.replace('__count__', String(count)) : this.dataset.empty, false);
        this.#fillVendors(pending, operation.signal);
      } catch (error) {
        // 利用者の操作による中断は表示を触らない。締め切り超過は伝える
        if (operation.signal.aborted && !timedOut) return;
        // 失敗しても再検索の操作を出すため、試行が終わった時点で検索済みとして扱う。
        this.#searched = true;
        let key = 'failed';
        if (timedOut) key = 'busy';
        else if (['busy', 'config'].includes(error.message)) key = error.message;
        this.#state(this.dataset[key], false);
      } finally {
        clearTimeout(slowNotice);
        clearTimeout(deadline);
      }
    }

    /** @param {string} endpoint @param {string} body @param {AbortSignal} signal */
    async #request(endpoint, body, signal) {
      for (let attempt = 0; attempt <= RETRY_LIMIT; attempt += 1) {
        signal.throwIfAborted();
        const controller = new AbortController();
        const abort = () => controller.abort();
        signal.addEventListener('abort', abort, { once: true });
        const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
        try {
          let response;
          try {
            response = await fetch(endpoint, {
              method: 'POST',
              // OPTIONSが400になるため、プリフライトを発生させないContent-Typeを使う。
              headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
              body, signal: controller.signal, credentials: 'omit', cache: 'no-store',
            });
          } catch (error) {
            // 実装バグの TypeError と区別するため、通信の失敗だけを再試行対象にする。
            if (error.name === 'AbortError') throw error;
            throw new Error('network');
          }
          if (response.status === 429 || response.status >= 500) throw new Error('busy');
          if (!response.ok) throw new Error('failed');
          const payload = await response.json();
          if (payload.error || payload.result?.isError) {
            const message = payload.error?.message || JSON.stringify(payload.result?.content);
            const temporary = /temporar|overload|rate.?limit|too many|service (?:unavailable|error)|internal (?:server )?error|timeout|timed out/i.test(message);
            throw new Error(temporary ? 'busy' : 'failed');
          }
          const products = payload.result?.structuredContent?.products;
          if (!Array.isArray(products)) throw new Error('failed');
          return products;
        } catch (error) {
          signal.throwIfAborted();
          const network = error.message === 'network';
          const retryable = network || error.message === 'busy' || error.name === 'AbortError';
          if (!retryable) throw error;
          if (attempt === RETRY_LIMIT) throw new Error(network ? 'failed' : 'busy');
        } finally {
          clearTimeout(timer);
          signal.removeEventListener('abort', abort);
        }
        await delay(RETRY_DELAY_MS * (attempt + 1), signal);
      }
    }

    /** @param {Array<{element: Element, card: Element, url: string}>} entries @param {AbortSignal} signal */
    #fillVendors(entries, signal) {
      // カタログの応答は販売元を持たないため、商品ごとの Ajax API で補う。
      // 既定 24 件を一度に取ると画面外の分まで通信するため、見えたものから取りに行く。
      // 販売元は補助情報のため、監視を用意できないときは出さないまま先へ進む
      if (signal.aborted || typeof IntersectionObserver !== 'function') return;

      const targets = entries.filter((entry) => entry.card && entry.element);

      if (targets.length === 0) return;

      const waiting = new Map(targets.map((entry) => [entry.card, entry]));
      const queue = [];
      let running = 0;

      const observer = new IntersectionObserver((records) => {
        for (const record of records) {
          if (!record.isIntersecting) continue;
          observer.unobserve(record.target);
          const entry = waiting.get(record.target);
          if (entry) queue.push(entry);
          waiting.delete(record.target);
        }
        pump();
        // rootMargin は root の矩形にしか効かないため、スクロールする祖先を root にする
      }, { root: this.results.closest('[data-image-content]'), rootMargin: VENDOR_ROOT_MARGIN });

      signal.addEventListener('abort', () => observer.disconnect(), { once: true });

      const fill = async ({ element, url }) => {
        try {
          const product = new URL(url);
          const response = await fetch(`${product.origin}${product.pathname}.js`, { signal, credentials: 'omit' });
          if (!response.ok) return;
          const data = await response.json();
          if (typeof data?.vendor !== 'string' || !data.vendor) return;
          element.textContent = data.vendor;
          element.hidden = false;
        } catch {
          // 中断も取得失敗も、販売元を出さないまま次へ進む
        }
      };

      const pump = () => {
        while (running < VENDOR_CONCURRENCY && queue.length && !signal.aborted) {
          running += 1;
          fill(queue.shift()).finally(() => {
            running -= 1;
            pump();
          });
        }
        if (waiting.size === 0 && queue.length === 0 && running === 0) observer.disconnect();
      };

      for (const { card } of targets) observer.observe(card);
    }

    /** @param {Array<object>} products @returns {{count: number, pending: Array<object>}} */
    #render(products) {
      const fragment = document.createDocumentFragment();
      const template = this.querySelector('[data-card]');
      const pending = [];
      const seen = new Set();
      for (const product of products) {
        if (!product || typeof product.title !== 'string' || !Array.isArray(product.variants)) continue;
        // seller は filters.shops で絞り込み済み。id が欠けた応答で全件落とさないよう、あるときだけ照合する。
        const variant = product.variants.find((item) => {
          const seller = item?.seller?.id;
          return (typeof seller !== 'string' || seller === this.dataset.shopId)
            && item?.availability?.available !== false && httpsUrl(item?.url);
        });
        if (!variant || seen.has(product.id || variant.url)) continue;
        const price = variant.price;
        if (!Number.isSafeInteger(price?.amount) || price.amount < 0 || !/^[A-Z]{3}$/.test(price.currency)) continue;
        let formattedPrice;
        try {
          const formatter = new Intl.NumberFormat(this.dataset.language, { style: 'currency', currency: price.currency });
          const digits = formatter.resolvedOptions().maximumFractionDigits;
          formattedPrice = formatter.format(price.amount / 10 ** digits);
        } catch {
          continue;
        }
        seen.add(product.id || variant.url);
        const card = template.content.cloneNode(true);
        card.querySelector('a').href = campaignUrl(variant.url);
        card.querySelector('h2').textContent = product.title;
        pending.push({
          element: card.querySelector('.c_p-page-image-search__vendor'),
          // 販売元は hidden で始まり大きさを持たないため、監視はカードに掛ける。
          card: card.querySelector('.c_p-page-image-search__card'),
          url: variant.url,
        });
        card.querySelector('.c_p-page-image-search__price').textContent = formattedPrice;
        const media = [...(Array.isArray(variant.media) ? variant.media : []), ...(Array.isArray(product.media) ? product.media : [])];
        const image = media.find((item) => item?.type === 'image' && httpsUrl(item.url));
        if (image) {
          const element = card.querySelector('img');
          element.src = sizedImageUrl(image.url);
          element.hidden = false;
        }
        fragment.append(card);
      }
      this.results.replaceChildren(fragment);
      return { count: seen.size, pending };
    }
  }

  if (!customElements.get('image-search')) customElements.define('image-search', ImageSearch);
})();
